/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Input } from "@nordcloud/gnui";
import { usePeopleQuery } from "~/generated/graphql";
import { InputLoader } from "~/components/Loaders";
import { useDisclosure } from "~/hooks";
import { mapInputStatus, isNotNil } from "~/tools";
import { ContactSelector } from "../ContactSelector";

type Props = Omit<React.HTMLAttributes<HTMLInputElement>, "onChange"> & {
  small?: boolean;
  value?: string[];
  onChange: (id: string) => void;
  placeholder?: string;
  name?: string;
  status?: "error" | "success";
  disabled?: boolean;
};

export function ContactsInput({
  value,
  onChange,
  placeholder,
  name,
  id,
  small,
  status,
  disabled = false,
  ...rest
}: Props) {
  const { isOpen, close, open } = useDisclosure();
  const { data, loading } = usePeopleQuery();

  const formMethods = useFormContext();
  const registerProps = name && formMethods ? formMethods.register(name) : {};

  const filteredPeople = useMemo(() => {
    const people = data?.people ?? [];

    return people.filter(isNotNil);
  }, [data]);

  const displayValue = useMemo(() => {
    return getContactPeopleNames(value, filteredPeople);
  }, [filteredPeople, value]);

  const apply = (selectedId: string) => {
    onChange(selectedId);
    close();
  };

  return (
    <>
      <InputLoader loading={loading}>
        <Input
          {...rest}
          {...registerProps}
          readOnly
          small={small}
          disabled={disabled}
          type="text"
          value={displayValue}
          name={name}
          id={id}
          placeholder={placeholder ?? "Contact Person"}
          status={mapInputStatus(status)}
          icon="sidebar"
          onClick={open}
        />
      </InputLoader>
      <ContactSelector
        isOpen={isOpen}
        options={filteredPeople}
        peopleLoading={loading}
        onClose={close}
        onSubmit={apply}
      />
    </>
  );
}

const getContactPeopleNames = (
  value?: string[],
  peopleData?: { id: string; name: string; email: string }[]
) => {
  if (!value) {
    return "";
  }

  const namesFromData = value.map((personId) => {
    const name = peopleData?.find((person) => person?.id === personId)?.name;

    return name ?? personId;
  });

  return namesFromData.join(", ");
};
